import React from 'react';
import { Button, ButtonProps, CircularProgress } from '@mui/material';

interface BrandButtonProps extends Omit<ButtonProps, 'variant'> {
  children: React.ReactNode;
  variant?: 'primary' | 'secondary' | 'outlined' | 'text';
  loading?: boolean;
  gradientType?: 'orange' | 'purple' | 'blue';
}

const BrandButton: React.FC<BrandButtonProps> = ({ 
  children,
  variant = 'primary',
  loading = false,
  gradientType = 'orange',
  disabled,
  sx,
  ...props 
}) => {
  const gradientMap = {
    orange: 'linear-gradient(135deg, #753403 0%, #C05A01 100%)',
    purple: 'linear-gradient(135deg, #4F076F 0%, #7E28A4 100%)',
    blue: 'linear-gradient(135deg, #12279E 0%, #314AD7 100%)',
  };

  const getButtonStyles = () => {
    switch (variant) {
      case 'primary':
        return {
          background: gradientMap[gradientType],
          color: '#FFFFFF',
          '&:hover': {
            background: gradientMap[gradientType],
            transform: 'translateY(-1px)',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
          },
          '&:disabled': {
            background: '#E8E8E8',
            color: '#8C8C8C',
            transform: 'none',
          },
        };
      case 'secondary':
        return {
          background: 'rgba(117, 52, 3, 0.1)',
          color: '#753403',
          border: '1px solid #753403',
          '&:hover': {
            background: 'rgba(117, 52, 3, 0.2)',
            transform: 'translateY(-1px)',
          },
        };
      case 'outlined':
        return {
          background: 'transparent',
          color: '#753403',
          border: '1px solid #FFB683',
          '&:hover': {
            background: '#FFF8F4',
            borderColor: '#753403',
          },
        };
      case 'text':
        return {
          background: 'transparent',
          color: '#753403',
          '&:hover': {
            background: 'rgba(117, 52, 3, 0.05)',
          },
        };
      default:
        return {};
    }
  };

  return (
    <Button
      disabled={loading || disabled}
      sx={{
        borderRadius: '12px',
        padding: '12px 24px',
        fontSize: '1rem',
        fontWeight: 600,
        textTransform: 'none',
        transition: 'all 0.2s ease-in-out',
        ...getButtonStyles(),
        ...sx,
      }}
      {...props}
    >
      {loading ? (
        <CircularProgress size={20} color="inherit" />
      ) : (
        children
      )}
    </Button>
  );
};

export default BrandButton;
